/**
 * What counts as a real, dialable number — the app side.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS CENTRAL AND NOT THREE INLINE CHECKS
 * ---------------------------------------------------------------------------
 *
 * Not every row in `users.phone` is a telephone number. A customer imported
 * from Business Central with no number on file holds '+91-BC-<code>', and
 * since 7 Oct a customer registered in the app with the number left blank
 * holds '+91-NP-<code>'. The column is NOT NULL, uniquely indexed, and IS the
 * login identity, so the absence has to be represented by something.
 *
 * Three screens had each grown their own `startsWith('+91-BC-')` test, and
 * `telLink` had none at all — so the Call button on a BC-imported shop with
 * no number produced `tel:+9100347` and rang a stranger, or nobody. Adding a
 * second placeholder form would have slipped past all three checks silently.
 *
 * So the rule lives here once, and it is POSITIVE: a number is dialable only
 * if it looks like an Indian mobile in E.164. Any placeholder, present or
 * future, any empty string, any landline, fails it without being named. A
 * negative rule fails open, and failing open here means ringing a number that
 * is not the shop's.
 *
 * This mirrors api/src/lib/phone.ts deliberately. Two copies of one rule is
 * not ideal; the alternative is a shared package for one regular expression,
 * and a drift between them shows up immediately as a number the server stored
 * and the app refuses to show.
 */

const DIALABLE = /^\+91[6-9]\d{9}$/;

/** Can this actually be rung or messaged? */
export function isDialable(phone: string | null | undefined): boolean {
  return typeof phone === 'string' && DIALABLE.test(phone);
}

/**
 * How a number is shown to a human.
 *
 * A placeholder rendered raw looks like a broken phone number, and somebody
 * will eventually try to ring it.
 */
export function displayPhone(phone: string | null | undefined): string {
  return isDialable(phone) ? (phone as string) : 'no phone';
}

/**
 * The ten digits an input field wants, or '' when there is no real number.
 *
 * Returning '' rather than the placeholder matters on an edit form: the field
 * reads "not set" and invites a real number, instead of inviting somebody to
 * edit a code into something that looks plausible.
 */
export function localDigits(phone: string | null | undefined): string {
  return isDialable(phone) ? (phone as string).replace(/\D/g, '').slice(-10) : '';
}

/**
 * What a phone INPUT should keep as the user types or PASTES.
 *
 * Strips every non-digit and keeps the LAST ten, so a pasted '+91 98765 43210',
 * '0098…', '0 98765 43210', or a plain ten digits all land as the same ten
 * local digits — the "10 digits OR +91 and 10 digits, and let me paste" rule,
 * applied at the one place the value is formed.
 *
 * The matching input must NOT cap maxLength at 10. A controlled input fires
 * onChange with the value the browser already truncated to maxLength, so a
 * +91 paste would lose its tail before this ever runs. Leave maxLength off the
 * phone fields (or well above 10) — that was the actual Manage/Profile paste bug.
 */
export function phoneInputDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(-10);
}
